import { useMemo, useState } from 'react';

import { useDebounced } from './use-debounced';

/** The longest search the server takes (`MAX_QUERY_LENGTH` of the audit search). */
export const JOURNAL_QUERY_MAX = 100;
/** How many `word~event` entries one request may carry (`MAX_TYPES` of the audit search). */
const MAX_TYPES = 240;
const SEARCH_DELAY_MS = 300;

/** The search sent with a journal request (both parts optional). */
export interface JournalQuery {
  q?: string | undefined;
  types?: readonly string[] | undefined;
}

/** An event identifier and its description in the viewer's language. */
export interface DescribedEvent {
  type: string;
  text: string;
}

const PLACEHOLDER = /\{\{[^}]*\}\}/g;

export function wordsOf(query: string): string[] {
  return [...new Set(query.trim().toLowerCase().split(/\s+/).filter(Boolean))];
}

/**
 * For each word of the query, the events whose description (in the viewer's language) contains
 * it, as `word~event_type` entries. The server cannot know the localized texts, so the client
 * names the events and the server satisfies a word by its own search (actor, identifier, object
 * names) OR by an event named for that word - every word still has to hold, so an actor name and
 * an action can be combined ("ivan group created"). Placeholders ("{{actor}}") are not part of a
 * description.
 */
export function matchingEventTypes(query: string, events: readonly DescribedEvent[]): string[] {
  const words = wordsOf(query).filter((word) => !word.includes('~'));
  if (words.length === 0) return [];
  const texts = events.map((event) => ({
    type: event.type,
    text: event.text.replace(PLACEHOLDER, ' ').toLowerCase(),
  }));
  const perWord = Math.floor(MAX_TYPES / words.length);
  const found: string[] = [];
  for (const word of words) {
    const types = texts.filter((event) => event.text.includes(word)).map((event) => event.type);
    for (const type of types.slice(0, perWord)) found.push(`${word}~${type}`);
  }
  return found;
}

/** Descriptions out of a flat i18n branch (`{ group_created: "Group created" }`). */
export function describedFrom(branch: unknown): DescribedEvent[] {
  if (typeof branch !== 'object' || branch === null) return [];
  return Object.entries(branch as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .map(([type, text]) => ({ type, text }));
}

export interface JournalSearch {
  /** What the field shows (updates on every key). */
  input: string;
  setInput: (value: string) => void;
  /** The settled query sent to the server: trimmed, bounded, `undefined` when empty. */
  q: string | undefined;
  /** `word~event` entries: the events each word of the settled query describes. */
  types: string[] | undefined;
  searching: boolean;
}

/** The search state of a journal: a debounced query plus the identifiers its words describe. */
export function useJournalSearch(events: readonly DescribedEvent[]): JournalSearch {
  const [input, setInput] = useState('');
  const settled = useDebounced(input, SEARCH_DELAY_MS).trim().slice(0, JOURNAL_QUERY_MAX);
  const types = useMemo(() => matchingEventTypes(settled, events), [settled, events]);
  return {
    input,
    setInput,
    q: settled || undefined,
    types: types.length > 0 ? types : undefined,
    searching: settled !== '',
  };
}
